/**
 * Journal API service
 */
import { apiClient } from './client';

// ============= Journal Entry Types =============

export interface JournalEntryCreate {
  title: string;
  content: string;
  mood_before?: string;
  mood_after?: string;
  energy_level?: number; // 1-10
  insights?: string;
  gratitude?: string;
  tags?: string[];
  intention_id?: string;
}

export interface JournalEntryUpdate {
  title?: string;
  content?: string;
  mood_before?: string;
  mood_after?: string;
  energy_level?: number;
  insights?: string;
  gratitude?: string;
  tags?: string[];
}

export interface JournalEntry {
  id: string;
  title: string;
  content: string;
  mood_before: string | null;
  mood_after: string | null;
  energy_level: number | null;
  insights: string | null;
  gratitude: string | null;
  tags: string[] | null;
  intention_id: string | null;
  is_private: boolean;
  created_at: string;
  updated_at: string;
}

export interface JournalEntryListResponse {
  entries: JournalEntry[];
  total: number;
  limit: number;
  offset: number;
}

// ============= Session Reflection Types =============

export interface SessionReflectionCreate {
  session_id: string;
  experience: string;
  effectiveness: number; // 1-5
  insights?: string;
  emotions_during?: string[];
  body_sensations?: string;
  relaxation_level?: number; // 1-10
  clarity_level?: number; // 1-10
  would_repeat?: boolean;
  action_items?: string;
  user_session_id?: string;
  intention_id?: string;
  session_duration?: number;
  completion_percentage?: number;
}

export interface SessionReflection {
  id: string;
  session_id: string;
  experience: string;
  effectiveness: number;
  insights: string | null;
  emotions_during: string[] | null;
  body_sensations: string | null;
  relaxation_level: number | null;
  clarity_level: number | null;
  would_repeat: boolean;
  action_items: string | null;
  session_duration: number | null;
  completion_percentage: number | null;
  created_at: string;
}

export interface SessionReflectionListResponse {
  reflections: SessionReflection[];
  total: number;
}

// ============= Prompt & Insights Types =============

export interface JournalPrompt {
  category: string;
  prompt: string;
  difficulty: string;
  date: string;
}

export interface MoodCount {
  mood: string;
  count: number;
}

export interface JournalPatterns {
  total_entries: number;
  consistency: number;
  common_moods: MoodCount[];
  average_energy: number;
  growth_indicators: string[];
  days_with_entries: number;
  gratitude_rate: number;
  insight_rate: number;
}

export interface SessionEffectivenessStats {
  session_id: string;
  average_effectiveness: number;
  repeat_rate: number;
  total_reflections: number;
}

export interface SessionEffectivenessAnalysis {
  total_reflections: number;
  average_effectiveness: number;
  most_effective_sessions: SessionEffectivenessStats[];
  recommendations: string[];
}

export interface JournalInsight {
  type: 'achievement' | 'positive' | 'suggestion';
  message: string;
  icon: string;
}

export interface JournalInsightsResponse {
  insights: JournalInsight[];
  patterns: JournalPatterns;
  session_effectiveness: SessionEffectivenessAnalysis;
}

// ============= API Functions =============

export const journalApi = {
  // ============= Journal Entries =============

  /**
   * Create a new journal entry (requires a subscription or the trial)
   */
  createEntry: async (data: JournalEntryCreate): Promise<{ status: string; entry_id: string }> => {
    const response = await apiClient.post<{ status: string; entry_id: string }>(
      '/journal/entry',
      data
    );
    return response.data;
  },

  /**
   * Get user's journal entries (requires a subscription or the trial)
   */
  getEntries: async (
    limit: number = 50,
    offset: number = 0,
    tag?: string
  ): Promise<JournalEntryListResponse> => {
    const response = await apiClient.get<JournalEntryListResponse>('/journal/entries', {
      params: { limit, offset, ...(tag && { tag }) },
    });
    return response.data;
  },

  /**
   * Get a specific journal entry
   */
  getEntry: async (entryId: string): Promise<JournalEntry> => {
    const response = await apiClient.get<JournalEntry>(`/journal/entry/${entryId}`);
    return response.data;
  },

  /**
   * Update a journal entry
   */
  updateEntry: async (entryId: string, data: JournalEntryUpdate): Promise<JournalEntry> => {
    const response = await apiClient.put<JournalEntry>(`/journal/entry/${entryId}`, data);
    return response.data;
  },

  /**
   * Delete a journal entry
   */
  deleteEntry: async (entryId: string): Promise<void> => {
    await apiClient.delete(`/journal/entry/${entryId}`);
  },

  // ============= Session Reflections =============

  /**
   * Create a session reflection
   */
  createReflection: async (
    data: SessionReflectionCreate
  ): Promise<{ status: string; reflection_id: string }> => {
    const response = await apiClient.post<{ status: string; reflection_id: string }>(
      '/journal/reflection',
      data
    );
    return response.data;
  },

  /**
   * Get user's session reflections
   */
  getReflections: async (
    sessionId?: string,
    limit: number = 50
  ): Promise<SessionReflectionListResponse> => {
    const response = await apiClient.get<SessionReflectionListResponse>('/journal/reflections', {
      params: { limit, ...(sessionId && { session_id: sessionId }) },
    });
    return response.data;
  },

  // ============= Prompts & Insights =============

  /**
   * Get daily journal prompt
   */
  getDailyPrompt: async (language: 'en' | 'me' = 'en'): Promise<JournalPrompt> => {
    const response = await apiClient.get<JournalPrompt>('/journal/prompt', {
      params: { language },
    });
    return response.data;
  },

  /**
   * Get journal insights (requires a subscription or the trial)
   */
  getInsights: async (): Promise<JournalInsightsResponse> => {
    const response = await apiClient.get<JournalInsightsResponse>('/journal/insights');
    return response.data;
  },
};

export default journalApi;
